import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ImagePlus, Sparkles, Users } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { PageHeader } from "@/shared/ui/PageHeader";
import { StepIndicator } from "@/shared/ui/StepIndicator";
import { Textarea } from "@/shared/ui/textarea";
import { AugmentPanel } from "@/shared/ui/augment-panel";
import { SlotMaterialsSection } from "@/shared/ui/slot-materials";
import { toast } from "@/shared/hooks/use-toast";
import { IMAGE_OR_VIDEO_ACCEPT } from "@/shared/lib/uploadImageFormats";
import { checkVideoFile } from "@/shared/lib/videoFileCheck";
import { SKU } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { characterService, type CharacterDto } from "@/entities/character/api/characterService";
import { errorToast } from "@/shared/lib/apiErrors";

const STEPS = ["起名与设定", "给它一张脸"] as const;

/**
 * 虚拟人建档流(0916 老板「新建虚拟人物也按照我们真人走,传照片和视频,这些可以让用户去生成」)。
 *
 * <p>★为什么不复用真人那条采集流:真人三步里有两步是**只对真人成立**的 ——
 * 人脸体检(虚拟立绘 0915 实测同样能过检测,但"这张脸够不够清楚"对画出来的人没有意义)
 * 与肖像授权存证(虚拟人没有肖像权人,没有可签的对象)。把它们塞进同一个组件意味着
 * 每一步都挂一个 `isReal &&`,两条流各自都变得更难读。形状一致 ≠ 代码合并。
 *
 * <p>★形状确实对齐:同样的步骤条、同样"照片和视频都能传"的素材槽、同样建完直落人物页。
 * 差别只在虚拟人多一条真人没有的路 —— <b>让 AI 从零把这个人画出来</b>
 * (`mode=create`,真人后端硬拒)。
 *
 * <p>★人物在第 1 步结束时就建出来了,不是最后一步才建:第 2 步的两条路(传素材 / AI 生成)
 * 都要一个真实的 characterId 才能落地(AI 补足是挂在人物上的任务)。所以这一页从第 2 步起
 * 顶上就写着"已建好,可以稍后再补" —— 中途离开不会丢人物,只是它暂时没有脸。
 */
export default function CharacterVirtualCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // 只认站内相对路径(与采集流同款白名单,避免开放重定向)。
  const returnTo = searchParams.get("returnTo")?.startsWith("/")
    ? searchParams.get("returnTo")
    : null;

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [appearance, setAppearance] = useState("");
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [augmentOpen, setAugmentOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { book } = usePriceBook();
  const augmentUnitCredits = book.unit(SKU.packAugment, "default");

  const detailQuery = useQuery({
    queryKey: ["characters", createdId],
    queryFn: () => characterService.get(createdId as number),
    enabled: createdId != null,
  });
  const character = detailQuery.data;
  const materials = character?.materials ?? [];

  const create = useMutation({
    mutationFn: () =>
      characterService.create({
        name: name.trim(),
        appearance: appearance.trim() || undefined,
        type: "virtual",
      }),
    onSuccess: (saved: CharacterDto) => {
      queryClient.invalidateQueries({ queryKey: ["characters"] });
      setCreatedId(saved.id);
      setStep(2);
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "创建失败" })),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["characters"] });
  };

  const upload = async (files: FileList) => {
    if (createdId == null || !files.length) return;
    setUploading(true);
    try {
      let firstId: number | null = null;
      for (const file of Array.from(files)) {
        // 视频走与真人线同一份体检闸(时长/大小/画幅/格式)—— 虚拟人的视频一样要过
        // 上游厂商素材墙,这条约束与人物真假无关。
        if (file.type.startsWith("video/")) {
          const issue = await checkVideoFile(file);
          if (issue?.blocksSubmit) {
            toast({
              title: `${file.name} 没通过体检`,
              description: issue.text,
              variant: "destructive",
            });
            continue;
          }
        }
        const m = await characterService.uploadMaterial(createdId, file, "identity");
        if (firstId == null) firstId = m.id;
      }
      // 第一份素材顺手钉成主素材(库墙卡面 + 后续 AI 补足的 i2i 锚都读它)。
      if (firstId != null && character?.primaryMaterialId == null) {
        await characterService.setPrimaryMaterial(createdId, firstId);
      }
      invalidate();
      void detailQuery.refetch();
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "上传失败" }));
    } finally {
      setUploading(false);
    }
  };

  const finish = () => {
    if (createdId == null) return;
    navigate(returnTo || `/characters/${createdId}`);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/characters"
        icon={Users}
        title="新建虚拟人"
        subtitle="动漫、插画等原创角色。建完用法和真人完全一样 —— 照片视频都能传,数字人和视频两条线都能用。"
      />

      <StepIndicator
        steps={STEPS}
        current={step}
        label="虚拟人建档流程"
        domainAttr="data-virtual-steps"
      />

      {step === 1 ? (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-1.5">
              <Label htmlFor="vc-name">名字 *</Label>
              <Input
                id="vc-name"
                value={name}
                maxLength={64}
                placeholder="如:小樱"
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vc-appearance">外观一句话</Label>
              <Textarea
                id="vc-appearance"
                value={appearance}
                maxLength={512}
                rows={3}
                placeholder="如:25 岁女性动漫角色,黑色齐肩短发,浅蓝色眼睛,白衬衫配深蓝针织背心"
                onChange={(e) => setAppearance(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                出片时逐字锁进画面主体描述(形象一致性的文字锚);下一步让 AI 画它时也会直接用上。
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                disabled={name.trim() === "" || create.isPending}
                data-testid="virtual-create-next"
                onClick={() => create.mutate()}
              >
                {create.isPending ? "创建中…" : "建好,下一步"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <p className="text-sm text-muted-foreground">
                「{name.trim()}」已经建好了。现在给它一张脸 —— 传现成的照片/视频,或者让 AI
                照着你写的外观画出来。也可以稍后在人物页再补。
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <ActionCard
                  icon={<ImagePlus className="h-5 w-5 text-primary" />}
                  title="传照片 / 视频"
                  desc="已经有立绘或做好的口播视频就直接传,和真人线同一个槽、同一套体检。"
                  cta={uploading ? "上传中…" : "选文件"}
                  testId="virtual-upload"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                />
                <ActionCard
                  icon={<Sparkles className="h-5 w-5 text-primary" />}
                  title="让 AI 画出来"
                  desc="写一句这个人长什么样,出 3 张定妆照挑 1 张作为这个人的底;之后所有补足都以它为准。"
                  cta="从零生成"
                  testId="virtual-augment"
                  onClick={() => setAugmentOpen(true)}
                />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_OR_VIDEO_ACCEPT}
                multiple
                className="hidden"
                aria-label="上传虚拟人素材"
                onChange={(e) => {
                  if (e.target.files?.length) void upload(e.target.files);
                  e.target.value = "";
                }}
              />
            </CardContent>
          </Card>

          {materials.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <SlotMaterialsSection
                  title="已有素材"
                  hint="第一份自动钉成主素材;更多编辑在人物页"
                  countUnit="项"
                  canWrite={false}
                  materials={materials}
                  primaryMaterialId={character?.primaryMaterialId}
                  pinBadgeText="主素材"
                />
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={finish}>
              稍后再传
            </Button>
            <Button disabled={materials.length === 0} data-testid="virtual-finish" onClick={finish}>
              完成,去人物页
            </Button>
          </div>
        </div>
      )}

      {createdId != null && character && (
        <AugmentPanel
          open={augmentOpen}
          onOpenChange={setAugmentOpen}
          title={`AI 生成 · ${character.name}`}
          modes={[
            {
              key: "create",
              label: "从零生成",
              hint: "写一句这个人长什么样,AI 出 3 张定妆照,挑 1 张作为这个人的底",
              briefPlaceholder:
                "如:25 岁女性动漫角色,黑色齐肩短发,浅蓝色眼睛,白衬衫配深蓝针织背心,干净清爽的二次元画风",
              estimatedCount: 3,
            },
          ]}
          slotLabels={{ identity: "定妆" }}
          queryKeyBase={["characters", createdId]}
          unitCredits={augmentUnitCredits}
          adapter={{
            start: (mode, slots, brief) =>
              characterService.augment(createdId, { mode, slots, brief }),
            fetchTask: (taskId) => characterService.augmentTask(createdId, taskId),
            curate: (taskId, picks) => characterService.curateFromTask(createdId, taskId, picks),
            listTasks: () => characterService.augmentTasks(createdId),
          }}
          onCurated={(created) => {
            // 生成的第一张就是这个人物的底 —— 钉成主素材,后续补足全部以它为 i2i 锚。
            if (character.primaryMaterialId == null && created.length > 0) {
              void characterService
                .setPrimaryMaterial(createdId, created[0].id)
                .then(() => invalidate());
            }
            void detailQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

/** 第 2 步的两条路各一张卡(与建人物类型选择同款形制)。 */
function ActionCard({
  icon,
  title,
  desc,
  cta,
  testId,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  cta: string;
  testId: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="flex flex-col gap-2 rounded-lg border p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/55 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-60"
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{desc}</p>
      <span className="mt-auto pt-1 text-xs font-medium text-primary">{cta} →</span>
    </button>
  );
}
